import { t } from "@hakili/i18n";
import type { Tone } from "@hakili/ui";

/**
 * `round F`, `F2`: **the vehicle's eleven stages, in ONE place, and the three
 * things that were wrong with them.**
 *
 * ## Why this file exists
 *
 * The stage vocabulary lived inside `vehicles.tsx`, which was right while one
 * screen read it. The dashboard reads it too now , it counts what is live, what
 * is sold and what has left the catalogue, and it names the stage of every car
 * in « Vos véhicules en cours » , and two copies of a vocabulary is how one
 * screen comes to call `paye` « Payé » while the other calls it « Fonds
 * reçus ». `P1-UIK-027`'s finding one level up: *the lists must differ; the
 * vocabulary must not.*
 *
 * ## Three defects found in moving it, all in the same direction
 *
 * The old map carried EIGHT entries under a comment that said *"the eleven
 * stages of 05 2.2.1"*, and the count was the tell.
 *
 * 1. **`reserve` is not a stage.** `catalog.VehicleStage` does not declare it
 *    and no api file writes it; `dealer.vehicules.etapeReserve` was a label for
 *    a value the wire cannot send. Removed, with its catalogue key.
 * 2. **Four real stages had no label at all** , `demande`, `visite`, `rapport`
 *    and `transaction`. The fallback *renders the stage as itself*, which is
 *    the right fallback and was doing exactly its job: a dealership whose car
 *    had a visit booked read the word « visite » on a chip. Four of the eleven
 *    states a car passes through were showing the wire's code to a customer.
 * 3. **Three labels disagreed with the drawing.** The mockup's own
 *    `ETAPES_VEHICULE` (`hakili-dealer.jsx:156-195`) says « Fonds reçus », not
 *    « Payé »; « Véhicule retiré par l'acheteur », not « Retiré »; « Vente
 *    bloquée, contrôle non conforme », not « Non conforme ». The difference is
 *    not decoration , « Payé » says money moved, « Fonds reçus » says the
 *    dealership has ITS money, and 05's own note is that Hakili never observes
 *    the dealer's leg.
 *
 * ## The order is the mockup's progression, and it is load-bearing
 *
 * `MAINLINE` is a progression and `TERMINAL` is not: a car that falls into a
 * terminal branch has no next stage, and the mockup keeps them in two lists for
 * that reason. Anything that walks stages in order , a stepper, a "what is
 * next" line , reads `MAINLINE` and nothing else.
 */

/** One stage, as the api sends it and as a person reads it. */
export interface StageDefinition {
  /** The wire value, verbatim from `catalog.VehicleStage`. */
  readonly id: string;
  /** The catalogue key for the full sentence. */
  readonly label: string;
  /** The catalogue key for the chip's shorter word, where the mockup gives one. */
  readonly short: string;
  readonly tone: Tone;
}

/**
 * The mainline, in the order a car travels it.
 *
 * **Keyed as DATA, never as object keys.** `enligne`, `nonConforme` and the
 * rest are wire values; `hakili/no-french-identifier` refuses them as
 * identifiers and is right to, because a rename would break the match with the
 * wire it must equal. They are strings in a list, which is what they are.
 */
export const MAINLINE: readonly StageDefinition[] = [
  { id: "enligne", label: "dealer.stage.enligne", short: "dealer.stage.enligneCourt", tone: "ok" },
  { id: "demande", label: "dealer.stage.demande", short: "dealer.stage.demandeCourt", tone: "attention" },
  { id: "visite", label: "dealer.stage.visite", short: "dealer.stage.visiteCourt", tone: "attention" },
  {
    id: "expertise",
    label: "dealer.stage.expertise",
    short: "dealer.stage.expertiseCourt",
    tone: "attention",
  },
  { id: "rapport", label: "dealer.stage.rapport", short: "dealer.stage.rapportCourt", tone: "attention" },
  {
    id: "transaction",
    label: "dealer.stage.transaction",
    short: "dealer.stage.transactionCourt",
    tone: "attention",
  },
  { id: "paye", label: "dealer.stage.paye", short: "dealer.stage.payeCourt", tone: "ok" },
  { id: "retire", label: "dealer.stage.retire", short: "dealer.stage.retireCourt", tone: "neutre" },
];

/**
 * The terminal branches. A car here has no next stage.
 *
 * They are OUT of `MAINLINE` deliberately , the mockup's own reason at
 * `:172-176`: *« un véhicule qui y tombe n'a pas d'étape suivante »*, so a
 * progression that contained them would offer a next step that does not exist.
 */
export const TERMINAL: readonly StageDefinition[] = [
  {
    id: "retireDuCatalogue",
    label: "dealer.stage.retireDuCatalogue",
    short: "dealer.stage.retireDuCatalogueCourt",
    tone: "neutre",
  },
  {
    id: "nonConforme",
    label: "dealer.stage.nonConforme",
    short: "dealer.stage.nonConformeCourt",
    tone: "alerte",
  },
  {
    id: "vendueHorsPlateforme",
    label: "dealer.stage.vendueHorsPlateforme",
    short: "dealer.stage.vendueHorsPlateformeCourt",
    tone: "alerte",
  },
];

/**
 * `round F4`: **`exitOf` is DELETED, and this note is what stood in its place.**
 *
 * It answered *how far did this car get before it left the line*, which the
 * stepper needs, from the BRANCH's declared exit , the drawing's own
 * `sortie` , because no door carried the fact. It was named an approximation
 * when it was written: *"a car withdrawn at `expertise` renders as though it
 * left at `enligne`. The exact answer is the journal's, and the day a door
 * carries the stage a car left from, this function reads it instead."*
 *
 * That day is this round. `catalog.vehicles.stage_before` is the record's own
 * answer, the withdrawal door writes it, and
 * `ck_vehicles__terminal_has_stage_before` refuses a terminal row without one.
 * (G5-3: the column is stamped on every transition, so it is read as an exit
 * only when the car's own stage is terminal.)
 * The page reads the column; the approximation is removed rather than left
 * beside it, because two answers to one question is how the wrong one survives.
 */

const BY_ID = new Map<string, StageDefinition>([...MAINLINE, ...TERMINAL].map((stage) => [stage.id, stage]));

/**
 * The stage's sentence, or **the stage itself** when the api names one this
 * build has not met.
 *
 * Printing a sentence the api never said is how a screen comes to describe a
 * machine it has not read , and this fallback is what surfaced defect 2 above,
 * by putting the raw word « visite » on a chip where a sentence belonged.
 */
/**
 * `T-2b`, `M0 (c)` (T-2's booking (4)): **a firm lock reads before the stage.** K-1's lock is a
 * column (`lock_state`), never a stage; the park's Civic read « En ligne » under a `ferme` lock. A
 * live car under a firm lock reads the lock's word; a sold or exited car reads its stage.
 *
 * `S4-10`: **a soft lock reads before the stage too, for the same reason.** The door this reads
 * from (`my-vehicles.controller.ts`) now sends `lockState` already passed through
 * `effectiveLockState`, so a soft lock already past its own deadline arrives here as `libre` ,
 * this function never sees a stale `soft` and needs no clock of its own.
 */
export function chipLabel(
  car: { readonly stage: string; readonly lockState?: string },
  short = false,
): string {
  if (car.lockState === "ferme" && isLive(car.stage)) return t("dealer.stage.reserveFerme");
  if (car.lockState === "soft" && isLive(car.stage)) return t("dealer.stage.reserveSoft");
  return short ? stageShort(car.stage) : stageLabel(car.stage);
}

export function chipTone(car: { readonly stage: string; readonly lockState?: string }): Tone {
  if (car.lockState === "ferme" && isLive(car.stage)) return "attention";
  if (car.lockState === "soft" && isLive(car.stage)) return "attention";
  return stageTone(car.stage);
}

export function stageLabel(stage: string): string {
  const found = BY_ID.get(stage);
  return found === undefined ? stage : t(found.label);
}

/** The chip's shorter word, on the same rule. */
export function stageShort(stage: string): string {
  const found = BY_ID.get(stage);
  return found === undefined ? stage : t(found.short);
}

/** The chip's tone. An unknown stage is neutral: a colour is a claim too. */
export function stageTone(stage: string): Tone {
  return BY_ID.get(stage)?.tone ?? "neutre";
}

/**
 * A car that has left the catalogue: the three terminal branches, exactly.
 *
 * **Unchanged from `vehicles.tsx`'s own set**, deliberately. `I4` built the
 * inventory's « Sorties » filter on these three and `vendueHorsPlateforme` is
 * one of them; quietly re-cutting the set from the dashboard would move a
 * filter's meaning under a screen this step is not building.
 */
export const EXITED_STAGES: ReadonlySet<string> = new Set(TERMINAL.map((stage) => stage.id));

/** Sold through Hakili, and gone. */
export const SOLD_STAGES: ReadonlySet<string> = new Set(["paye", "retire"]);

/**
 * **What still WEIGHS on the dealership**, which is not the same set as what is
 * unsold, and the difference is one stage.
 *
 * The drawing's own `EN_CHARGE` (`hakili-dealer.jsx:892`) keeps `paye` and drops
 * `retire`, and it is right: funds received is not the end of the dealership's
 * work , `enregistrerRetrait` is still owed, the buyer has not been handed his
 * car, and 06 §6.1.7 gives that gesture to both rungs. Only `retire` ends it.
 *
 * `isLive` answers a different question , *is this car still unsold* , and the
 * inventory's « Actifs » filter is right to use it. Counting a team's workload
 * with it drops every car sitting between the money and the handover, which on
 * a real forecourt is the pile somebody is chasing.
 */
export function weighsOnTheTeam(stage: string): boolean {
  return stage !== "retire" && !EXITED_STAGES.has(stage);
}

/** Still working: neither sold nor out of the catalogue. */
export function isLive(stage: string): boolean {
  return !SOLD_STAGES.has(stage) && !EXITED_STAGES.has(stage);
}
